import type { AtomicAsset, BackingInfo } from "../types";

/**
 * The AtomicAssets API exposes a `backed_tokens` array on any asset that
 * still has protocol-level backing locked in (pre-deprecation mints, or
 * anything minted before you flip your collection over to the escrow
 * flow). Returns the first backed token in human-readable form, or null if
 * the asset isn't backed.
 */
export function getBackingInfo(asset: AtomicAsset): BackingInfo | null {
  const backed = asset.backed_tokens?.[0];
  if (!backed) return null;

  const amount = (Number(backed.amount) / 10 ** backed.token_precision).toFixed(
    backed.token_precision
  );

  return {
    symbol: backed.token_symbol,
    amount,
    precision: backed.token_precision,
  };
}

export function formatAssetName(asset: AtomicAsset): string {
  return asset.name || (asset.data?.name as string) || `Asset #${asset.asset_id}`;
}

/**
 * Raw AtomicAssets image field (usually a bare IPFS hash, sometimes already
 * a full http(s) URL) — not resolved to a gateway URL. Feed this into
 * `<IpfsImage hash={...} />`, which handles the gateway list and per-visitor
 * fallback (see components/IpfsImage.tsx for why that matters — a single
 * hardcoded gateway silently breaks images for anyone whose network or
 * browser extensions block that one gateway).
 */
export function assetImageHash(asset: AtomicAsset): string | undefined {
  return (asset.data?.img as string) || (asset.immutable_data?.img as string) || undefined;
}
